import type { AIImageConfig } from './types';

class AIImageManager {
  private static instance: AIImageManager;
  private config: AIImageConfig = {
    baseUrl: '',
    globalPrompt: '',
    timeout: 30000,
    retryCount: 3
  };

  private constructor() {}

  static getInstance(): AIImageManager {
    if (!AIImageManager.instance) {
      AIImageManager.instance = new AIImageManager();
    }
    return AIImageManager.instance;
  }

  setConfig(config: Partial<AIImageConfig>): void {
    this.config = { ...this.config, ...config };
  }

  getConfig(): AIImageConfig {
    return { ...this.config };
  }

  async generateImage(params: any): Promise<any> {
    const { baseUrl, globalPrompt } = this.config;

    if (!baseUrl) {
      throw new Error('baseUrl is not configured. Please call AIImage.config() first.');
    }

    const finalPrompt = globalPrompt
      ? `${globalPrompt}, ${params.user_input}`
      : params.user_input;

    const requestBody = {
      ...params,
      user_input: finalPrompt
    };

    const response = await fetch(`${baseUrl}/api/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`Failed to generate image: ${response.statusText}`);
    }

    const contentType = response.headers.get('content-type');

    // Check if response is JSON
    if (contentType && contentType.includes('application/json')) {
      const jsonData = await response.json();

      // Return full JSON response for record_id
      return jsonData;
    }

    // Handle binary image response
    const blob = await response.blob();
    return {
      url: URL.createObjectURL(blob)
    };
  }

  async modifyImage(recordId: number, modificationPrompt: string): Promise<any> {
    const { baseUrl } = this.config;

    if (!baseUrl) {
      throw new Error('baseUrl is not configured. Please call AIImage.config() first.');
    }

    const response = await fetch(`${baseUrl}/api/modify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        record_id: recordId,
        modification_prompt: modificationPrompt
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to modify image: ${response.statusText}`);
    }

    return await response.json();
  }

  async regenerateImage(recordId: number): Promise<any> {
    const { baseUrl } = this.config;

    if (!baseUrl) {
      throw new Error('baseUrl is not configured. Please call AIImage.config() first.');
    }

    const response = await fetch(`${baseUrl}/api/modify`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        record_id: recordId,
        modification_prompt: '',
        is_regenerate: true
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to regenerate image: ${response.statusText}`);
    }

    return await response.json();
  }
}

export default AIImageManager;
